import {Component, computed, inject, Input, OnInit, signal} from '@angular/core';
import {SelectionModel} from '@angular/cdk/collections';
import {FuseDeleteDialogComponent} from '../../../../core/components/delete-dialog/delete-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ToastrService} from 'ngx-toastr';
import {Router} from '@angular/router';
import {fuseAnimations} from '../../../../core/animations';
import {LearningPathsService} from '../../../../core/services/learningPaths.service';
import {TranslateService} from '@ngx-translate/core';
import {ELearningService} from '../../../../core/services/e-learning.service';
import {CertificateTemplateService} from '../../../../core/services/certificateTemplate.service';
import {DocumentsService} from '../../../../core/services/documents.service';
import {finalize} from 'rxjs/operators';
import {CertificateSetting} from '../../../../core/components/certificate-setting/certificate-setting.component';

@Component({
  selector: 'elearning-participant',
  templateUrl: './elearning-participant.component.html',
  styleUrls: ['./elearning-participant.component.scss'],
  animations: fuseAnimations,
  standalone: false
})
export class ELearningParticipantComponent implements OnInit{
  private certificateTemplateService = inject(CertificateTemplateService);
  private documentService = inject(DocumentsService);

  @Input() participants;
  @Input() certificates;
  private elearningData = signal<Partial<CertificateSetting> | null>(null);
  @Input() set elearning(value: Partial<CertificateSetting> | null) {
    this.elearningData.set(value);
  }
  readonly displayedColumns = computed(() => [
    'select',
    'name',
    'organization',
    'targetGroup',
    'createdAt',
    'percentage',
    ...(this.certificateEnabled()
      ? ['certificate', 'sendCertificate']
      : []),
    'deleteFromParticipant'
  ]);
  selection = new SelectionModel<any>(true , []);
  checkedUsers = [];
  selectedVal = 'hide';
  certificateEnabled = computed(() => this.certificateTemplateService.certificateEnabled() &&
    this.elearningData()?.certificateAllowed === true && !!this.elearningData()?.certificateTemplateId);
  sendingCertificates = new Set<number>();

  certificatesMap = new Map<string, any>();

  constructor(public dialog: MatDialog, private toastr: ToastrService, private router: Router,
              private translate: TranslateService, public elService: ELearningService) {
  }

  ngOnInit() {
    console.log(this.participants);
    for (const certificate of (this.certificates || [])) {
      if (certificate.elearningId && certificate.frontUserId) {
        this.certificatesMap.set(certificate.frontUserId.toString(), certificate);
      }
    }
    console.log(this.certificatesMap);
  }

  masterToggle () {
    this.isAllSelected() ?
      this.selection.clear() :
      this.participants.forEach(row => this.selection.select(row));
    this.checkedUsers = this.selection.selected;
  }

  isAllSelected () {
    const numSelected = this.selection.selected.length;
    const numRows = this.participants.length;
    return numSelected === numRows;
  }

  checkUser (user , event) {
    if (event.checked) {
      this.checkedUsers.push(user);
    } else {
      let index = this.checkedUsers.findIndex(x => +x.id === +user.id);
      if (index !== -1) {
        this.checkedUsers.splice(index , 1);
      }
    }
  }

  deleteFromParticipant (event , participant) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(FuseDeleteDialogComponent , { panelClass: 'unsave-form-dialog',
      width: '300px' , height: 'auto',
      data: {title: 'Delete' , message: 'elearning.removeFromParticipantMsg'}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (!participant?.frontUserId || !participant?.elearningId) {
          let title = this.translate.instant('failure');
          let msg = this.translate.instant('elearning.removeFromParticipantError');
          this.toastr.error(msg , title);
          return;
        }
        this.elService.deleteParticipant(participant.frontUserId, participant.elearningId).subscribe({
          next: () => {
            let index = this.participants.findIndex(x => +x.id === +participant.id);
            if (index !== -1) {
              this.participants = this.participants.filter((pt, i) => i !== index);
            }
            let title = this.translate.instant('successfully');
            let msg = this.translate.instant('elearning.removeFromParticipantSuccess');
            this.toastr.success(msg , title);
          },
          error: () => {
            let title = this.translate.instant('failure');
            let msg = this.translate.instant('elearning.removeFromParticipantError');
            this.toastr.error(msg , title);
          }
        });
      }
    });
  }

  openDialog (user): void {

  }

  getCertificateDocumentUrl(name: string) {
    return this.documentService.getFileStream(name);
  }

  sendCertificate(event: MouseEvent, participant) {
    event.stopPropagation();
    if (!this.certificateEnabled() || !participant?.frontUserId || !participant?.elearningId ||
      this.sendingCertificates.has(participant.id)) {
      return;
    }
    this.sendingCertificates.add(participant.id);
    const dialogRef = this.dialog.open(FuseDeleteDialogComponent, {
      panelClass: 'unsave-form-dialog', width: '300px', height: 'auto',
      data: {title: 'tasks.send_certificate', message: 'tasks.send_certificate_msg'}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        this.sendingCertificates.delete(participant.id);
        return;
      }
      this.certificateTemplateService.createElearningCertificate(participant.frontUserId, participant.elearningId)
        .pipe(finalize(() => this.sendingCertificates.delete(participant.id)))
        .subscribe({
          next: () => {
            this.toastr.success(this.translate.instant('tasks.send_certificate_success'));
            this.elService.getELearningById(participant.elearningId).subscribe({
              next: (res: {elearningParticipants: {id: number; certificate?: {document?: {name: string}}}[]}) => {
                const updatedParticipant = res.elearningParticipants?.find(user => +user.id === +participant.id);
                if (updatedParticipant) {
                  participant.certificate = updatedParticipant.certificate;
                }
              },
              error: () => this.toastr.error(this.translate.instant('tasks.load_error'))
            });
          },
          error: () => this.toastr.error(this.translate.instant('failure'))
        });
    });
  }

  communicationAction(event) {
    if (event.value === 'emailList') {
      this.selectedVal = event.value;
      let emails = [];
      for (let i = 0; i < this.checkedUsers.length; i++) {
        if (this.checkedUsers[i].frontUser && this.checkedUsers[i].frontUser.email) {
          emails.push(this.checkedUsers[i].frontUser.email);
        }
        if (this.checkedUsers[i].email) {
          emails.push(this.checkedUsers[i].email);
        }
      }
      if (!emails.length) {
        this.toastr.error('Ausgewählte User haben keine E-Mail' , 'Error!');
        setTimeout(() => {
          this.selectedVal = 'hide';
        }, 500);
        return;
      }
      const to = emails[0];
      emails.shift();
      const cc = '';
      const bcc = emails.join(',');
      const subject = 'Betreff der E-Mail';
      const body = 'Inhalt der E-Mail...';

      this.generateEmltlFile(to, cc, bcc, subject, body);
      setTimeout(() => {
        this.selectedVal = 'hide';
      }, 500);
      return;
    }
    if (event.value === 'mail') {
      let emails = [];
      for (let i = 0; i < this.checkedUsers.length; i++ ) {
        if (this.checkedUsers[i].frontUser && this.checkedUsers[i].frontUser.email) {
          emails.push(this.checkedUsers[i].frontUser.email);
        }
        if (this.checkedUsers[i].email) {
          emails.push(this.checkedUsers[i].email);
        }
      }
      let uniqueSet = new Set(emails);
      emails = [...uniqueSet];
      this.router.navigate(['/pages/communications'], {queryParams: {
          type: 'mail',
          subtype: 'participant',
          ids: emails.toString()
        }});
    } else {
      let ids = [];
      let guestIds = [];
      for (let i = 0; i < this.checkedUsers.length; i++ ) {
        if (this.checkedUsers[i].frontUserId) {
          ids.push(this.checkedUsers[i].frontUserId);
        }
        if (this.checkedUsers[i].guestUserId) {
          guestIds.push(this.checkedUsers[i].guestUserId);
        }
      }
      this.router.navigate(['/pages/communications'], {queryParams: {
          type: event.value,
          ids: ids.toString(),
          guestIds: guestIds.toString()
        }});
    }
  }
  generateEmltlFile(to, cc, bcc, subject, body) {
    const emlContent = `To: ${to}\r\nCc: ${cc || ''}\r\nBcc: ${bcc || ''}\r\nSubject: ${subject}\r\nX-Unsent: 1\r\nContent-Type: text/html\r\n\r\n<html><body>${body}</body></html>`;
    const blob = new Blob([emlContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'email.eml';
    a.click();

    URL.revokeObjectURL(url);
  }
}
